import { useMemo } from 'react'
import { useFirestoreQuery } from '../../../../../firebase/useFirestore'
import usePersonaReadFailure from './usePersonaReadFailure'
import personaProfileFromDocs from '../../../../../helpers/liveReportHelpers/personaProfileFromDocs'
import { PERSONA_DATA_FIELD_PREFIX, isForbiddenPersonaDataTitle } from '../../../../../helpers/liveReportHelpers/personaWidgetTypes'

// De gegevens voor 'Traject en gegevens' in de app: de deelnemer, zijn traject
// en zijn gegevens, alleen wat de gekozen velden nodig hebben. Een eigen hook
// zodat de widget op de openbare link (T12) dezelfde weergave kan voeden met
// `personaData.profile` van de server, in dezelfde vorm.
// AI-CONTEXT: See docs/features/livereport-per-deelnemer.md (T9)
// AI-CONTEXT: See docs/pitfalls.md#database-firestore-silent-listener-failure

/**
 * @typedef {'idle' | 'loading' | 'error' | 'missing' | 'ready'} PersonaProfileStatus
 * idle = geen deelnemer; missing = de deelnemer bestaat niet (meer).
 */

/**
 * @param {string|null} personaId - Uit useWidgetPersona
 * @param {Array<string>} fields - `Settings.fields`
 * @returns {{ status: PersonaProfileStatus, profile: Object<string, *> }}
 */
const usePersonaProfile = (personaId, fields) => {
    const { failed, onError } = usePersonaReadFailure(personaId)

    const keys = Array.isArray(fields) ? fields : []
    const needsRoute = keys.includes('Route')
    // Een identificatienummer wordt nooit getoond, dus daarvoor niets lezen (net als de server).
    const needsData = keys.some((key) => typeof key === 'string' && key.startsWith(PERSONA_DATA_FIELD_PREFIX)
        && !isForbiddenPersonaDataTitle(key.slice(PERSONA_DATA_FIELD_PREFIX.length)))

    // Geen orderBy: die laat documenten zonder het veld weg
    // (docs/pitfalls.md#database-firestore-orderby-excludes-missing-field).
    const personas = useFirestoreQuery('Personas', {
        where: [['ID', '==', personaId || '']],
        enabled: !!personaId,
        onError,
    })
    const persona = Array.isArray(personas) ? personas[0] || null : null
    const routeId = persona && persona.RouteID ? persona.RouteID : ''

    const routes = useFirestoreQuery('PersonaRoutes', {
        where: [['ID', '==', routeId]],
        enabled: needsRoute && !!routeId,
        onError,
    })
    const dataRows = useFirestoreQuery('PersonaData', {
        where: [['PersonaID', '==', personaId || '']],
        enabled: !!personaId && needsData,
        onError,
    })

    const fieldsKey = JSON.stringify(keys)
    return useMemo(() => {
        if (!personaId) return { status: 'idle', profile: {} }
        if (failed) return { status: 'error', profile: {} }
        // '' = nog niet geladen (de begintoestand van useFirestoreQuery).
        if (!Array.isArray(personas)) return { status: 'loading', profile: {} }
        if (!persona) return { status: 'missing', profile: {} }
        const routesPending = needsRoute && !!routeId && !Array.isArray(routes)
        const dataPending = needsData && !Array.isArray(dataRows)
        if (routesPending || dataPending) return { status: 'loading', profile: {} }
        return {
            status: 'ready',
            profile: personaProfileFromDocs({
                fields: keys,
                persona,
                routes: Array.isArray(routes) ? routes : [],
                dataRows: Array.isArray(dataRows) ? dataRows : [],
            }),
        }
    // `keys` volgt uit fieldsKey; een inline array als dependency zet de memo elke render opnieuw.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personaId, failed, personas, persona, routes, dataRows, needsRoute, needsData, routeId, fieldsKey])
}

export default usePersonaProfile
